import { DeleteOutlined } from "@ant-design/icons";
import { Button, Image, Upload, message } from "antd";
import {
  addUploadedFile,
  removeUploadedFile,
} from "pages/Notification/notificationMediaSlice";
import { useDispatch, useSelector } from "react-redux";
import { UploadAttachments } from "themes/Icon";

const { Dragger } = Upload;

const FileUploadComponent = ({ data, disabled = false }) => {
  const dispatch = useDispatch();
  const uploadedFiles = useSelector(
    (state) => state.notificationMedia.uploadedFiles
  );

  const draggerProps = {
    beforeUpload: (file) => {
      const isAllowedType = [
        "application/pdf",
        "video/mp4",
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/svg+xml",
      ].includes(file.type);

      if (!isAllowedType) {
        message.error(`${file.name} is not a supported file type.`);
        return Upload.LIST_IGNORE;
      }

      dispatch(addUploadedFile(file));
      return false;
    },
    multiple: true,
    showUploadList: false,
  };

  const handleRemove = (file) => {
    dispatch(removeUploadedFile(file));
  };

  const getFileIcon = (type) => {
    if (!type) return null;
    if (type.includes("pdf"))
      return (
        <img
          src="https://cdn-icons-png.flaticon.com/512/337/337946.png"
          alt="pdf"
          width={32}
          height={32}
        />
      );
    if (type.includes("mp4") || type.includes("video"))
      return (
        <img
          src="https://cdn-icons-png.flaticon.com/512/1161/1161738.png"
          alt="video"
          width={32}
          height={32}
        />
      );
    if (type.includes("image"))
      return (
        <img
          src="https://cdn-icons-png.flaticon.com/512/337/337948.png"
          alt="image"
          width={32}
          height={32}
        />
      );
    return null;
  };

  const existingFiles = data?.files || [];
  const imageFiles = existingFiles.filter((f) =>
    f?.notification_file_mimetype?.startsWith("image/")
  );
  const otherFiles = existingFiles.filter(
    (f) => f?.notification_file_mimetype && !f.notification_file_mimetype.startsWith("image/")
  );

  const handleDownload = (file) => {
    const link = document.createElement("a");
    link.href = file?.notification_s3_path;
    link.download = file?.notification_file_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (disabled) {
    const hasContent = imageFiles.length > 0 || otherFiles.length > 0;
    if (!hasContent) return null;

    return (
      <div>
        <label className="input-label">Attachments</label>
        <div className="mt-2">
          {imageFiles.length > 0 && (
            <Image.PreviewGroup>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: otherFiles.length > 0 ? 16 : 0,
                }}
              >
                {imageFiles.map((file, index) => (
                  <Image
                    key={file?.notification_media_id || index}
                    src={file?.notification_s3_path}
                    alt={file?.notification_file_name}
                    width={imageFiles.length === 1 ? "100%" : 140}
                    height={imageFiles.length === 1 ? "auto" : 140}
                    style={{
                      objectFit: "cover",
                      borderRadius: 10,
                      maxHeight: imageFiles.length === 1 ? 320 : 140,
                    }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk2tG8fAAAAABJRU5ErkJggg=="
                  />
                ))}
              </div>
            </Image.PreviewGroup>
          )}

          {otherFiles.map((file, index) => (
            <div
              key={file?.notification_media_id || index}
              style={{
                background: "#f5f5f5",
                padding: "12px 16px",
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {getFileIcon(file?.notification_file_mimetype)}
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {file?.notification_file_name}
                  </div>
                  {file?.notification_file_size > 0 && (
                    <div style={{ fontSize: 12, color: "#555" }}>
                      {file.notification_file_size_formatted || `${(file.notification_file_size / 1024).toFixed(1)} KB`}
                    </div>
                  )}
                </div>
              </div>
              <button
                style={{
                  border: "none",
                  background: "none",
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "var(--Dark, #121212)",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "130%",
                }}
                onClick={() => handleDownload(file)}
              >
                Download
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="input-label">Upload files</label>
      <div className="mt-2">
        <Dragger {...draggerProps} style={{ background: "#fff", padding: 20 }}>
          <p className="ant-upload-drag-icon flex justify-center">
            <UploadAttachments />
          </p>
          <p className="ant-upload-text">Click to upload or drag and drop</p>
          <p className="ant-upload-hint">
            SVG, PNG, JPG, GIF, PDF, MP4 (max. 800×400px)
          </p>
        </Dragger>

        {existingFiles.length > 0 && (
          <div className="uploaded-files-list" style={{ marginTop: 20 }}>
            {existingFiles.map((file, index) => (
              <div
                key={file?.notification_media_id || index}
                style={{
                  background: "#f5f5f5",
                  padding: "12px 16px",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {getFileIcon(file?.notification_file_mimetype)}
                  <div>
                    <div style={{ fontWeight: 500 }}>
                      {file?.notification_file_name}
                    </div>
                    {file?.notification_file_size > 0 && (
                      <div style={{ fontSize: 12, color: "#555" }}>
                        {file.notification_file_size}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {uploadedFiles.length > 0 && (
          <div className="uploaded-files-list" style={{ marginTop: 20 }}>
            {uploadedFiles.map((file) => (
              <div
                key={file.uid}
                style={{
                  background: "#f5f5f5",
                  padding: "12px 16px",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {getFileIcon(file.type)}
                  <div>
                    <div style={{ fontWeight: 500 }}>{file.name}</div>
                    {file?.size !== 0 && (
                      <div style={{ fontSize: 12, color: "#555" }}>
                        {(file?.size / 1024)?.toFixed(0)} KB
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemove(file)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploadComponent;
